import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import { dropTask, all } from 'ember-concurrency';
import RouteTemplate from 'ember-route-template';
import SortableGroup from 'ember-sortable/components/sortable-group';
import { and, eq, gt, not } from 'ember-truth-helpers';
import EmberWormhole from 'ember-wormhole/components/ember-wormhole';
import SignInKioskDemo from 'garaje/components/homepage/kiosk-demo/sign-in-demo';
import SetupGuideSteps from 'garaje/components/setup-guide-steps';
import MessageBox from 'garaje/components/ui/classic/message-box';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiWell from 'garaje/components/ui/well';
import FlowCard from 'garaje/components/visitors/settings/visitor-types/flow-card';
import type FlowModel from 'garaje/models/flow';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { FLOW_TYPE } from 'garaje/utils/enums';
import { filterBy, or } from 'macro-decorators';

import type { VisitorTypesIndexRouteModel } from './route';

interface VisitorsSettingsVisitorTypesIndexSignature {
  Args: {
    model: VisitorTypesIndexRouteModel;
  };
  Element: HTMLDivElement;
}

class VisitorsSettingsVisitorTypesIndex extends Component<VisitorsSettingsVisitorTypesIndexSignature> {
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;

  @filterBy('args.model.allFlowsForCurrentLocation', 'enabled', true) enabledFlows!: FlowModel[];
  @filterBy('flows', 'type', FLOW_TYPE.GLOBAL_CHILD) globalChildFlows!: FlowModel[];

  @or('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin!: boolean;

  @tracked showIpadDemoModal = false;

  get flows(): FlowModel[] {
    const { allFlowsForCurrentLocation: flows } = this.args.model;
    return flows.filter(
      ({ type }) => isBlank(type) || type.toLowerCase() === 'flows' || type === FLOW_TYPE.GLOBAL_CHILD,
    );
  }

  get walkupFlow(): FlowModel | undefined {
    const { allFlowsForCurrentLocation: flows } = this.args.model;
    return flows.find(({ type }) => type === FLOW_TYPE.PROPERTY_WALKUP);
  }

  get locationIsConnectedToProperty(): boolean {
    return <number>this.args.model.connectedTenants.length > 0;
  }

  get showSetupGuide(): boolean {
    return this.isAdmin && this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper');
  }

  get flowsWithNoPhoneNumbersAsArray(): FlowModel[] {
    const { flowsWithNoPhoneNumberRequired: flows } = this.args.model;
    if (!flows) {
      return [];
    }
    return flows.slice();
  }

  get isUserEligibleForModal(): boolean {
    return this.featureFlags.isEnabled('growth_ipad_demo');
  }

  showModal = (): void => {
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      this.showIpadDemoModal = true;
    }
  };

  closeModal = (): void => {
    if (this.featureFlags.isEnabled('growth_ipad_demo')) {
      this.showIpadDemoModal = false;
    }
  };

  updateFlowPositions = dropTask(async (flows: FlowModel[]) => {
    await all(
      flows.map((flow, index) => {
        flow.position = index + 1;
        return flow.save();
      }),
    );
  });

  <template>
    {{#if this.showSetupGuide}}
      <EmberWormhole @to="onboarding">
        <SetupGuideSteps />
      </EmberWormhole>
    {{/if}}

    <UiPage>
      <div class="flex items-center">
        <div class="flex-auto min-w-0 min-h-0">
          <UiHeader>Sign-in flow</UiHeader>
        </div>
        <div class="flex-none" data-test-new-flow>
          {{#if (can "edit visitor-types")}}
            <LinkTo @route="visitors.settings.visitor-types.new" class="btn btn--brand">
              Add visitor type
            </LinkTo>
          {{/if}}
        </div>
      </div>

      {{#if
        (and (can "access-visitors-emergency-features communication") (gt this.flowsWithNoPhoneNumbersAsArray.length 0))
      }}
        <MessageBox @type="info">
          To ensure you can send visitor emergency notifications, require phone numbers for the following sign in flows:
          <ul style="list-style-type: disc; padding-left: 20px;">
            {{#each this.flowsWithNoPhoneNumbersAsArray as |flow|}}
              <li>{{flow.name}}</li>
            {{/each}}
          </ul>
        </MessageBox>
      {{/if}}

      <UiWell as |Well|>
        {{#if (eq this.enabledFlows.length 0)}}
          <Well.Item>
            <MessageBox
              @type="error"
              @header="No visitor types are enabled for this location"
              data-test-no-enabled-flows-warning
            >
              <p class="mb-0">
                Please enable at least one visitor type.
              </p>
            </MessageBox>
          </Well.Item>
        {{else if (and this.globalChildFlows.length (not this.currentAdmin.isGlobalAdmin))}}
          <Well.Item>
            <MessageBox
              @type="info"
              @header="This location has globally managed sign-in flows"
              data-test-globally-managed-flow
            >
              <p class="mb-0">
                If you have questions or need to make changes, please contact a global admin.
              </p>
            </MessageBox>
          </Well.Item>
        {{/if}}

        {{#let (can "edit visitor-types") as |canEdit|}}
          <SortableGroup
            class="-mb-4"
            @model={{this.flows}}
            @onChange={{this.updateFlowPositions.perform}}
            @disabled={{not canEdit}}
            data-test-sortable-group
            as |group|
          >
            {{#each (sortBy "position" group.model) as |flow|}}
              <group.item @model={{flow}} @group={{group}} class="mb-4 trans-all" as |item|>
                <Well.Item>
                  <FlowCard @flow={{flow}} @enabledFlows={{this.enabledFlows}} />
                  <item.handle
                    class="{{if canEdit 'icon-reSort' 'opacity-0 pointer-events-none'}}"
                    aria-hidden={{if canEdit false true}}
                    data-test-handle
                    data-test-handle-for={{flow.name}}
                  />
                </Well.Item>
              </group.item>
            {{/each}}

            {{#if this.locationIsConnectedToProperty}}
              {{#if this.walkupFlow}}
                <Well.Item>
                  <FlowCard @flow={{this.walkupFlow}} />
                </Well.Item>
              {{/if}}
            {{/if}}
          </SortableGroup>
        {{/let}}
      </UiWell>
      {{#if this.showIpadDemoModal}}
        <SignInKioskDemo @isDemoInstance={{false}} @demoName="signin" @onExternalClose={{this.closeModal}} />
      {{/if}}

      {{#if this.isUserEligibleForModal}}
        <p class="text-sm mb-0">Learn more about
          <a href="https://help.envoy.com/visitor-types/" target="_blank" tabindex="-1" rel="noopener">customizing
            sign-in flows</a>
          with multiple visitor types or a
          <span role="button" class="text-gem-50" {{on "click" this.showModal}}>demo</span>
          of the kiosk experience.</p>
      {{else}}
        <p class="text-sm mb-0">Learn more about
          <a href="https://help.envoy.com/visitor-types/" target="_blank" tabindex="-1" rel="noopener">customizing
            sign-in flows</a>
          with multiple visitor types.</p>
      {{/if}}

    </UiPage>
  </template>
}

export default RouteTemplate(VisitorsSettingsVisitorTypesIndex);
